import { useTranslation } from 'react-i18next';
import { Button } from '@hero/web-components';
import { PencilIcon } from '@hero/web-icons';
import type { SectionStatus } from './ClientPeekInfo';
import { StatusPill } from './ClientPeekInfo';

/**
 * Small coloured dot indicating section status. Used in outline rails next
 * to a section title where a full StatusPill would be too heavy.
 */
export function StatusDot({ status }: { status: SectionStatus }) {
  const cls =
    status.variant === 'complete'
      ? 'bg-primary'
      : status.variant === 'partial'
        ? 'bg-amber-500'
        : 'bg-gray-300';
  return <span className={`inline-block w-2 h-2 rounded-full ${cls}`} />;
}

/**
 * Read-only label/value pair. Falls back to a muted "—" or the provided
 * `empty` string when the value is nullish, empty string, or empty array.
 */
export function FieldDisplay({
  label,
  value,
  empty,
}: {
  label: string;
  value?: React.ReactNode;
  empty?: string;
}) {
  const isEmpty =
    value === undefined ||
    value === null ||
    value === '' ||
    (Array.isArray(value) && value.length === 0);
  return (
    <div className="space-y-1">
      <div className="text-xs font-medium text-muted-foreground">{label}</div>
      {isEmpty ? (
        <div className="text-sm text-gray-400 italic">{empty ?? '—'}</div>
      ) : (
        <div className="text-sm text-gray-900">{value}</div>
      )}
    </div>
  );
}

/**
 * Dashed empty-state row for a section that has no data yet. Optionally
 * shows an "+ Add" button that calls `onAdd` (disabled when `onAdd` absent).
 */
export function EmptySectionHint({
  label,
  onAdd,
  addLabel,
}: {
  label: string;
  onAdd?: () => void;
  addLabel?: string;
}) {
  const { t } = useTranslation(['m-client']);
  return (
    <div className="flex items-center justify-between py-6 px-4 rounded-md bg-gray-50 border border-dashed border-gray-200">
      <div className="text-sm text-muted-foreground italic">{label}</div>
      <Button variant="outline" size="sm" disabled={!onAdd} onClick={onAdd}>
        + {addLabel ?? t('page.edit_section')}
      </Button>
    </div>
  );
}

export interface SectionCardProps {
  /** DOM id used for outline-rail jump links and `scroll-mt-*` tuning. */
  id?: string;
  title: string;
  status: SectionStatus;
  /** When provided, renders a "Edit section" pencil button in the header. */
  onEdit?: () => void;
  /**
   * Tighter padding suited for nested surfaces (e.g. the workspace Info tab
   * where the outer page already has its own padding). Defaults to false.
   */
  compact?: boolean;
  children: React.ReactNode;
}

/**
 * Generic editable section container — used on `ClientPage` and on the
 * workspace Info tab. Header: title + StatusPill + optional Edit button.
 * Body: free-form children (typically a grid of FieldDisplay / EditableField).
 */
export function SectionCard({
  id,
  title,
  status,
  onEdit,
  compact,
  children,
}: SectionCardProps) {
  const { t } = useTranslation(['m-client']);
  return (
    <section
      id={id}
      className="scroll-mt-24 rounded-lg border border-gray-200 bg-white">
      <header
        className={`flex items-center justify-between gap-3 ${
          compact ? 'px-4 py-2.5' : 'px-5 py-3'
        } border-b border-gray-100`}>
        <div className="flex items-center gap-3">
          <h2
            className={`font-semibold text-gray-900 ${
              compact ? 'text-sm' : 'text-base'
            }`}>
            {title}
          </h2>
          <StatusPill status={status} />
        </div>
        {onEdit ? (
          <Button variant="ghost" size="sm" onClick={onEdit}>
            <PencilIcon className="h-4 w-4" />
            {t('page.edit_section')}
          </Button>
        ) : null}
      </header>
      <div className={compact ? 'px-4 py-3' : 'px-5 py-4'}>{children}</div>
    </section>
  );
}
